import React, { ReactNode, useEffect, useState } from 'react'
import * as ReactBootstrap from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBarcode,
  faBuilding,
  faCalendarAlt,
  faCalendarCheck,
  faMobileAlt,
  faSave,
  faTimes,
  faUser,
} from '@fortawesome/free-solid-svg-icons'

import { AutoSuggestField } from './AutoSuggestField'

export interface SupportPackage {
  url?: string
  device_serial_number: string
  imei1?: string | null
  imei2?: string | null
  start_date: string
  end_date: string
  sold_by?: string | null
  sold_on?: string | null
  organization: string
}

const EMPTY: SupportPackage = {
  device_serial_number: '',
  imei1: '',
  imei2: '',
  start_date: '',
  end_date: '',
  sold_by: '',
  sold_on: '',
  organization: '',
}

interface SupportPackageFormProps {
  initial?: SupportPackage
  submitting: boolean
  onSubmit: (values: SupportPackage) => void
  onCancel: () => void
}

const userLabel = (user: any): string =>
  `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()

const organizationLabel = (org: any): string => `${org.code} - ${org.name}`

// Rich dropdown row for staff: name on top, email + role beneath.
const renderUserSuggestion = (user: any): ReactNode => {
  const name = userLabel(user) || user.email
  return (
    <div className="d-flex flex-column">
      <span className="fw-semibold">{name}</span>
      <small className="text-muted">
        {user.email}
        {user.role ? ` · ${user.role}` : ''}
      </small>
    </div>
  )
}

// Small building blocks that give the form a consistent enterprise look.
const Section: React.FC<{ icon: any; title: string; children: ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <section className="mb-4">
    <div className="d-flex align-items-center text-uppercase text-secondary fw-bold small mb-3">
      <FontAwesomeIcon icon={icon} className="me-2 text-primary" />
      {title}
    </div>
    {children}
  </section>
)

const FieldLabel: React.FC<{
  icon: any
  children: ReactNode
  required?: boolean
}> = ({ icon, children, required }) => (
  <ReactBootstrap.Form.Label className="fw-semibold small text-secondary mb-1">
    <span className="me-2 text-muted">
      <FontAwesomeIcon icon={icon} />
    </span>
    {children}
    {required ? <span className="text-danger ms-1">*</span> : null}
  </ReactBootstrap.Form.Label>
)

export const SupportPackageForm: React.FC<SupportPackageFormProps> = ({
  initial,
  submitting,
  onSubmit,
  onCancel,
}) => {
  const [values, setValues] = useState<SupportPackage>(initial ?? EMPTY)
  const [orgError, setOrgError] = useState(false)

  useEffect(() => {
    setValues(initial ?? EMPTY)
    setOrgError(false)
  }, [initial])

  const update = (field: keyof SupportPackage, value: string) =>
    setValues((prev) => ({ ...prev, [field]: value }))

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    // Organization is set only on autosuggest selection (not free typing),
    // so guard it here instead of HTML `required` — which would wrongly block
    // edits where the input starts blank but the value is already set.
    if (!values.organization) {
      setOrgError(true)
      return
    }
    onSubmit(values)
  }

  return (
    <ReactBootstrap.Form onSubmit={handleSubmit}>
      <Section icon={faBarcode} title="Device">
        <ReactBootstrap.Form.Group
          className="mb-3"
          controlId="sp-device-serial"
        >
          <FieldLabel icon={faBarcode} required>
            Device serial number
          </FieldLabel>
          <ReactBootstrap.Form.Control
            required
            placeholder="e.g. C02X123ABCDE"
            value={values.device_serial_number}
            onChange={(e) => update('device_serial_number', e.target.value)}
          />
        </ReactBootstrap.Form.Group>

        <ReactBootstrap.Row>
          <ReactBootstrap.Col md={6}>
            <ReactBootstrap.Form.Group className="mb-3" controlId="sp-imei1">
              <FieldLabel icon={faMobileAlt}>IMEI 1</FieldLabel>
              <ReactBootstrap.Form.Control
                placeholder="15-digit IMEI"
                value={values.imei1 ?? ''}
                onChange={(e) => update('imei1', e.target.value)}
              />
            </ReactBootstrap.Form.Group>
          </ReactBootstrap.Col>
          <ReactBootstrap.Col md={6}>
            <ReactBootstrap.Form.Group className="mb-3" controlId="sp-imei2">
              <FieldLabel icon={faMobileAlt}>IMEI 2</FieldLabel>
              <ReactBootstrap.Form.Control
                placeholder="Optional"
                value={values.imei2 ?? ''}
                onChange={(e) => update('imei2', e.target.value)}
              />
            </ReactBootstrap.Form.Group>
          </ReactBootstrap.Col>
        </ReactBootstrap.Row>
      </Section>

      <Section icon={faCalendarAlt} title="Coverage period">
        <ReactBootstrap.Row>
          <ReactBootstrap.Col md={6}>
            <ReactBootstrap.Form.Group
              className="mb-3"
              controlId="sp-start-date"
            >
              <FieldLabel icon={faCalendarAlt} required>
                Start date
              </FieldLabel>
              <ReactBootstrap.Form.Control
                required
                type="date"
                value={values.start_date ?? ''}
                onChange={(e) => update('start_date', e.target.value)}
              />
            </ReactBootstrap.Form.Group>
          </ReactBootstrap.Col>
          <ReactBootstrap.Col md={6}>
            <ReactBootstrap.Form.Group className="mb-3" controlId="sp-end-date">
              <FieldLabel icon={faCalendarCheck} required>
                End date
              </FieldLabel>
              <ReactBootstrap.Form.Control
                required
                type="date"
                value={values.end_date ?? ''}
                onChange={(e) => update('end_date', e.target.value)}
              />
            </ReactBootstrap.Form.Group>
          </ReactBootstrap.Col>
        </ReactBootstrap.Row>
      </Section>

      <Section icon={faUser} title="Sale details">
        <ReactBootstrap.Row>
          <ReactBootstrap.Col md={6}>
            <AutoSuggestField
              label="Sold by"
              endpoint="users"
              icon={<FontAwesomeIcon icon={faUser} />}
              placeholder="Search staff by name..."
              helpText="Pick a staff member or type a name."
              initialText={initial?.sold_by ?? ''}
              getSuggestionLabel={userLabel}
              renderSuggestion={renderUserSuggestion}
              onSelect={(user) => update('sold_by', userLabel(user))}
              onTextChange={(text) => update('sold_by', text)}
            />
          </ReactBootstrap.Col>
          <ReactBootstrap.Col md={6}>
            <ReactBootstrap.Form.Group className="mb-3" controlId="sp-sold-on">
              <FieldLabel icon={faCalendarAlt}>Sold on</FieldLabel>
              <ReactBootstrap.Form.Control
                type="date"
                value={values.sold_on ?? ''}
                onChange={(e) => update('sold_on', e.target.value)}
              />
            </ReactBootstrap.Form.Group>
          </ReactBootstrap.Col>
        </ReactBootstrap.Row>
      </Section>

      <Section icon={faBuilding} title="Assignment">
        <AutoSuggestField
          label="Organization"
          endpoint="organizations"
          required
          icon={<FontAwesomeIcon icon={faBuilding} />}
          placeholder="Search organizations..."
          helpText="Select the service centre this package belongs to."
          getSuggestionLabel={organizationLabel}
          onSelect={(org) => {
            update('organization', org.url)
            setOrgError(false)
          }}
        />
        {orgError && (
          <div className="text-danger small">
            Please select an organization from the suggestions.
          </div>
        )}
      </Section>

      <div className="d-flex justify-content-end gap-2 border-top pt-3">
        <ReactBootstrap.Button
          variant="light"
          type="button"
          onClick={onCancel}
          disabled={submitting}
        >
          <FontAwesomeIcon icon={faTimes} className="me-2" />
          Cancel
        </ReactBootstrap.Button>
        <ReactBootstrap.Button
          variant="primary"
          type="submit"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <ReactBootstrap.Spinner
                as="span"
                animation="border"
                size="sm"
                className="me-2"
              />
              Saving...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faSave} className="me-2" />
              Save package
            </>
          )}
        </ReactBootstrap.Button>
      </div>
    </ReactBootstrap.Form>
  )
}
